import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const styles = {
  dark: {
    panel: "mt-5 rounded-2xl border border-white/10 bg-white/[0.025] p-4 sm:p-5",
    notice: "text-slate-400",
    consent: "border-white/15 bg-white/[0.04] text-slate-300 accent-[#D4AF37]",
    input: "border-white/10 bg-white/[0.04] text-slate-100 placeholder:text-slate-500 focus:border-[#D4AF37]/60",
    muted: "text-slate-500 hover:text-slate-300",
    primary: "bg-[#D4AF37] text-[#0A0B0E] shadow-[0_0_30px_rgba(212,175,55,0.16)] hover:shadow-[0_0_42px_rgba(212,175,55,0.26)]",
  },
  light: {
    panel: "mt-5 rounded-2xl border border-[#14201B]/10 bg-[#F8F6F0] p-4 sm:p-5",
    notice: "text-[#14201B]/60",
    consent: "border-[#14201B]/15 bg-white text-[#14201B]/70 accent-[#0E5C43]",
    input: "border-[#14201B]/10 bg-white text-[#14201B] placeholder:text-[#14201B]/35 focus:border-[#0E5C43]/60",
    muted: "text-[#14201B]/45 hover:text-[#14201B]/75",
    primary: "bg-[#0E5C43] text-white shadow-[0_16px_36px_rgba(14,92,67,0.18)] hover:bg-[#0c4f3a]",
  },
};

const messageFromError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" && detail.length < 220 ? detail : fallback;
};

const createLeadDraftId = () => (
  typeof window !== "undefined" && window.crypto?.randomUUID
    ? window.crypto.randomUUID().replace(/-/g, "")
    : `draft${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`
);

const reportGoogleAdsVerifiedLead = (verifiedSessionId) => {
  if (!verifiedSessionId || typeof window === "undefined") return;
  const storageKey = `google_ads_verified_lead_${verifiedSessionId}`;
  try {
    if (window.sessionStorage.getItem(storageKey) === "1") return;
    if (typeof window.gtag_report_conversion !== "function") return;
    window.gtag_report_conversion();
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // Analytics must never block an already confirmed lead from being shown to the visitor.
  }
};

const hasCompleteDraftContact = (form) => {
  const phone = String(form?.messenger || "").replace(/\D/g, "");
  const isUaeMobile = /^5\d{8}$/.test(phone);
  if (String(form?.direction || "").toLowerCase() === "callback") return isUaeMobile;
  return Boolean(
    String(form?.name || "").trim()
    && String(form?.company || "").trim()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form?.email || "").trim())
    && isUaeMobile,
  );
};

export const useLeadOtp = ({ form, resetForm, t }) => {
  const [stage, setStage] = useState("form");
  const [consent, setConsent] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [suffix, setSuffix] = useState("");
  const [completion, setCompletion] = useState("code");
  const [actionUrl, setActionUrl] = useState("");
  const [otpRequired, setOtpRequired] = useState(true);
  const [modeLoaded, setModeLoaded] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const [verificationError, setVerificationError] = useState("");
  const [draftId, setDraftId] = useState(createLeadDraftId);
  const [draftState, setDraftState] = useState("idle");
  const lastSavedDraft = useRef("");
  const draftSnapshot = JSON.stringify({
    name: form?.name || "", company: form?.company || "", email: form?.email || "",
    messenger: form?.messenger || "", direction: form?.direction || "", volume: form?.volume || "", message: form?.message || "",
  });

  useEffect(() => {
    let active = true;
    axios.get(`${API}/leads/otp/status`)
      .then((response) => {
        if (active) setOtpRequired(response.data?.required !== false);
      })
      .catch(() => {
        // Failing closed keeps verification required until the server can be reached.
        if (active) setOtpRequired(true);
      })
      .finally(() => { if (active) setModeLoaded(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!modeLoaded || !hasCompleteDraftContact(form) || lastSavedDraft.current === `${draftSnapshot}|${consent}`) return undefined;
    let active = true;
    setDraftState("saving");
    const timer = window.setTimeout(async () => {
      try {
        await axios.put(`${API}/leads/drafts`, { ...form, messenger_type: "whatsapp", draft_id: draftId, consent, page_url: window.location.href });
        if (active) {
          lastSavedDraft.current = `${draftSnapshot}|${consent}`;
          setDraftState("saved");
        }
      } catch {
        if (active) setDraftState("error");
      }
    }, 900);
    return () => { active = false; window.clearTimeout(timer); };
  }, [consent, draftId, draftSnapshot, form, modeLoaded]);

  const resetOtp = (newDraft = false) => {
    setStage("form");
    setConsent(false);
    setSessionId("");
    setCode("");
    setSuffix("");
    setCompletion("code");
    setActionUrl("");
    setRetryAfterSeconds(0);
    setVerificationError("");
    if (newDraft) {
      setDraftId(createLeadDraftId());
      setDraftState("idle");
      lastSavedDraft.current = "";
    }
  };

  const start = async () => {
    if (!consent) {
      toast.error(t("contact.otpConsentRequired"));
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API}/leads/otp/start`, {
        ...form,
        messenger_type: "whatsapp",
        consent: true,
        page_url: window.location.href,
        draft_id: draftId,
      });
      setSessionId(response.data.session_id);
      setSuffix(response.data.destination_suffix || "");
      setCompletion(response.data.completion === "inbound" ? "inbound" : "code");
      setActionUrl(typeof response.data.action_url === "string" ? response.data.action_url : "");
      setCode("");
      setRetryAfterSeconds(Number(response.data.resend_after_seconds || 180));
      setVerificationError("");
      setStage("verify");
      toast.success(response.data.completion === "inbound" ? t("contact.otpWhatsAppReady") : t("contact.otpCodeSent"));
    } catch (error) {
      toast.error(t("contact.otpStartError"), { description: messageFromError(error, t("contact.otpStartErrorDesc")) });
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!/^\d{4,8}$/.test(code)) {
      toast.error(t("contact.otpCodeInvalid"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads/otp/verify`, { session_id: sessionId, code });
      reportGoogleAdsVerifiedLead(sessionId);
      toast.success(t("contact.successTitle"), { description: t("contact.successDesc") });
      resetForm();
      resetOtp(true);
    } catch (error) {
      const detail = messageFromError(error, t("contact.otpVerifyErrorDesc"));
      setVerificationError(detail);
      toast.error(t("contact.otpVerifyError"), { description: detail });
    } finally {
      setBusy(false);
    }
  };

  const checkInbound = useCallback(async (silent = false) => {
    if (!sessionId || busy) return;
    if (!silent) setBusy(true);
    try {
      const response = await axios.get(`${API}/leads/otp/${encodeURIComponent(sessionId)}/status`);
      if (response.data?.status !== "submitted") return;
      toast.success(t("contact.successTitle"), { description: t("contact.successDesc") });
      resetForm();
      resetOtp(true);
    } catch (error) {
      if (!silent) toast.error(t("contact.otpVerifyError"), { description: messageFromError(error, t("contact.otpVerifyErrorDesc")) });
    } finally {
      if (!silent) setBusy(false);
    }
  }, [busy, resetForm, sessionId, t]);

  useEffect(() => {
    if (stage !== "verify" || completion !== "inbound" || !sessionId) return undefined;
    const timer = window.setInterval(() => { checkInbound(true); }, 5000);
    return () => window.clearInterval(timer);
  }, [stage, completion, sessionId, checkInbound]);

  useEffect(() => {
    if (retryAfterSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setRetryAfterSeconds((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfterSeconds]);

  const submit = async () => {
    if (!consent) {
      toast.error(t("contact.otpConsentRequired"));
      return;
    }
    if (otpRequired) {
      if (stage === "verify" && completion === "inbound") await checkInbound();
      else if (stage === "verify") await verify();
      else await start();
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        messenger_type: "whatsapp",
        consent: true,
        page_url: window.location.href,
      });
      toast.success(t("contact.successTitle"), { description: t("contact.successDesc") });
      resetForm();
      resetOtp(true);
    } catch (error) {
      toast.error(t("contact.errorTitle"), { description: messageFromError(error, t("contact.errorDesc")) });
    } finally {
      setBusy(false);
    }
  };

  return { stage, consent, setConsent, code, setCode, suffix, completion, actionUrl, busy, otpRequired, modeLoaded, retryAfterSeconds, verificationError, draftState, submit, start, resetOtp };
};

export const LeadOtpVerification = ({ otp, t, variant = "dark", directSubmitLabel }) => {
  const theme = styles[variant] || styles.dark;
  const verificationStage = otp.stage === "verify";
  const inboundApproval = verificationStage && otp.completion === "inbound";
  const retryCountdown = `${Math.floor(otp.retryAfterSeconds / 60)}:${String(otp.retryAfterSeconds % 60).padStart(2, "0")}`;

  if (!otp.modeLoaded) {
    return (
      <div className={theme.panel} data-testid="lead-submission-mode-loading">
        <p className={`text-xs leading-relaxed ${theme.notice}`}>{t("contact.otpStatusLoading")}</p>
      </div>
    );
  }

  if (!otp.otpRequired) {
    return (
      <div className={theme.panel} data-testid="lead-direct-submit-panel">
        <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-xs leading-relaxed ${theme.consent}`}>
          <input
            type="checkbox"
            checked={otp.consent}
            onChange={(event) => otp.setConsent(event.target.checked)}
            data-testid="lead-consent-checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded"
          />
          <span>{t("contact.directConsent")}</span>
        </label>
        <button
          type="submit"
          disabled={!otp.modeLoaded || otp.busy || !otp.consent}
          data-testid="lead-direct-submit-button"
          className={`group mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-semibold transition-all duration-300 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${theme.primary}`}
        >
          {otp.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : directSubmitLabel || t("contact.submit")}
        </button>
      </div>
    );
  }

  return (
    <div className={theme.panel} data-testid="lead-otp-panel">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#D4AF37]/12">
          {verificationStage ? <MessageCircle className="h-4 w-4 text-[#D4AF37]" /> : <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />}
        </span>
        <div>
          <p className="text-sm font-medium text-[#F3E5AB]">{inboundApproval ? t("contact.otpInboundTitle") : verificationStage ? t("contact.otpVerifyTitle") : t("contact.otpTitle")}</p>
          <p className={`mt-1 text-xs leading-relaxed ${theme.notice}`}>
            {inboundApproval
              ? t("contact.otpInboundPrompt")
              : verificationStage
                ? `${t("contact.otpCodePrompt")} ${otp.suffix ? `•••• ${otp.suffix}` : ""}`
                : t("contact.otpNotice")}
          </p>
        </div>
      </div>

      {!verificationStage ? (
        <>
        <label className={`mt-4 flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-xs leading-relaxed ${theme.consent}`}>
          <input
            type="checkbox"
            checked={otp.consent}
            onChange={(event) => otp.setConsent(event.target.checked)}
            data-testid="lead-consent-checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded"
          />
          <span>{t("contact.otpConsent")}</span>
        </label>
        {otp.draftState !== "idle" && (
          <p className={`mt-2 text-xs leading-relaxed ${otp.draftState === "error" ? "text-rose-300" : theme.notice}`} data-testid="lead-draft-save-status">
            {otp.draftState === "saving" ? t("contact.draftSaving") : otp.draftState === "saved" ? t("contact.draftSaved") : t("contact.draftSaveError")}
          </p>
        )}
        </>
      ) : inboundApproval ? (
        <>
          {otp.actionUrl && <a href={otp.actionUrl} target="_blank" rel="noopener noreferrer" data-testid="lead-otp-whatsapp-continue" className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${theme.input}`}>{t("contact.otpContinueWhatsApp")}</a>}
          <button type="button" onClick={otp.resetOtp} disabled={otp.busy} data-testid="lead-otp-change-number" className={`mt-3 text-xs underline-offset-4 transition-colors hover:underline disabled:opacity-60 ${theme.muted}`}>{t("contact.otpChangeNumber")}</button>
        </>
      ) : (
        <>
          <input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            value={otp.code}
            onChange={(event) => otp.setCode(event.target.value.replace(/\D/g, ""))}
            placeholder={t("contact.otpCodePh")}
            data-testid="lead-otp-code-input"
            className={`mt-4 w-full rounded-xl border px-4 py-3.5 text-center text-base tracking-[0.35em] outline-none transition-colors ${theme.input}`}
          />
          <button type="button" onClick={otp.resetOtp} disabled={otp.busy} data-testid="lead-otp-change-number" className={`mt-3 text-xs underline-offset-4 transition-colors hover:underline disabled:opacity-60 ${theme.muted}`}>{t("contact.otpChangeNumber")}</button>
        </>
      )}

      {verificationStage && (
        <div className={`mt-4 rounded-xl border p-3 text-xs leading-relaxed ${theme.consent}`} data-testid="lead-otp-fallback-notice">
          <p>{t("contact.otpSmsFallback")}</p>
          <button
            type="button"
            onClick={otp.start}
            disabled={otp.busy || otp.retryAfterSeconds > 0}
            data-testid="lead-otp-request-new-code"
            className={`mt-2 font-semibold underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60 ${theme.muted}`}
          >
            {otp.retryAfterSeconds > 0 ? `${t("contact.otpRetryCountdown")} ${retryCountdown}` : t("contact.otpRequestNewCode")}
          </button>
          <p className={`mt-2 ${theme.notice}`}>{t("contact.otpSupportEmail")}</p>
        </div>
      )}

      {verificationStage && otp.verificationError && (
        <p className="mt-3 text-xs leading-relaxed text-rose-300" data-testid="lead-otp-error-help">
          {otp.verificationError} {t("contact.otpSupportEmail")}
        </p>
      )}

      <button
        type="submit"
        disabled={otp.busy || (!verificationStage && !otp.consent)}
        data-testid={verificationStage ? (inboundApproval ? "lead-otp-check-button" : "lead-otp-verify-button") : "lead-otp-start-button"}
        className={`group mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-semibold transition-all duration-300 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${theme.primary}`}
      >
        {otp.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : inboundApproval ? t("contact.otpCheckApproval") : verificationStage ? t("contact.otpVerifySubmit") : t("contact.otpStartSubmit")}
      </button>
    </div>
  );
};
